import React, { useEffect, useMemo, useState } from "react";
import {
  Activity,
  ArrowUpRight,
  BarChart3,
  Cpu,
  Droplets,
  Plus,
  Thermometer,
  Wifi,
  WifiOff,
} from "lucide-react";
import { toast } from "sonner";
import { PageShell, PageHero, PageSection } from "../components/PageShell";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useAuth } from "../context/AuthContext";
import { usePopup } from "../context/PopupContext";
import { logError, logInfo } from "../utils/logger";
import {
  addDeviceForUser,
  renameUserDevice,
  subscribeDeviceLive,
  subscribeUserDevices,
  parseDeviceLastSeen,
  resolveDeviceOnline,
} from "../services/deviceService";
import PumpControl from "../components/PumpControl";
import { useUserRole } from "../hooks/useUserRole";
import {
  deviceTypeLabel,
  devicePreviewSensors,
  inferDeviceType,
  resolvePumpConnected,
} from "../utils/deviceCapabilities";

export default function Dashboard() {
  const [deviceID, setDeviceID] = useState("");
  const [deviceName, setDeviceName] = useState("");
  const [userDevices, setUserDevices] = useState({});
  const [deviceData, setDeviceData] = useState({});
  const [editingDeviceId, setEditingDeviceId] = useState("");
  const [editingDeviceName, setEditingDeviceName] = useState("");
  const [statusTick, setStatusTick] = useState(() => Date.now());
  const { user } = useAuth();
  const { effectiveRole } = useUserRole();
  const { showPopup } = usePopup();
  const canControlPump =
    effectiveRole === "owner" ||
    effectiveRole === "operator" ||
    effectiveRole === "admin" ||
    effectiveRole === "super_admin";

  const cards = useMemo(() => {
    const entries = Object.entries(userDevices);
    return entries
      .map(([id, device]) => {
        const live = deviceData[id] || {};
        const lastSeen = parseDeviceLastSeen(live);
        const isSimulator = !!device?.isSimulator;
        const online = resolveDeviceOnline(live, isSimulator, statusTick);
        const deviceType = inferDeviceType(id, device?.deviceType);
        const pumpConnected = resolvePumpConnected({
          deviceId: id,
          deviceType,
          pumpConnected: device?.pumpConnected,
        });
        return {
          id,
          name: device?.name || id,
          isSimulator,
          deviceType,
          pumpConnected,
          hubId: device?.hubId || "",
          online,
          lastSeen,
          t: live?.Temperature,
          h: live?.Humidity,
          s: live?.SoilMoisture,
        };
      })
      .filter((item) => {
        // Linked pot nodes only show under the hub card (Devices page)
        if (item.deviceType === "node" && item.hubId) return false;
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [deviceData, userDevices, statusTick]);

  useEffect(() => {
    const timer = setInterval(() => setStatusTick(Date.now()), 10000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!user) return;
    const liveUnsubs = [];
    const unsubUserDevices = subscribeUserDevices(
      user.uid,
      (devices) => {
        setUserDevices(devices);

        Object.keys(devices).forEach((id) => {
          const unsubLive = subscribeDeviceLive(id, (dataSnap) => {
            setDeviceData((prev) => ({
              ...prev,
              [id]: dataSnap || {},
            }));
          });
          liveUnsubs.push(unsubLive);
        });
      },
      (error) => {
        logError("Dashboard", "Failed to read user devices", error, { uid: user.uid });
        showPopup({
          type: "error",
          title: "Device Load Failed",
          message: "Unable to load devices. Please try again shortly.",
        });
      }
    );

    return () => {
      unsubUserDevices();
      liveUnsubs.forEach((fn) => fn());
    };
  }, [showPopup, user]);

  const handleAddDevice = async () => {
    if (!deviceID.trim() || !deviceName.trim()) {
      showPopup({
        type: "warning",
        title: "Missing Fields",
        message: "Please fill both Device ID and Device Name."
      });
      return;
    }

    try {
      if (!user) {
        showPopup({
          type: "error",
          title: "Authentication Required",
          message: "Please login again before adding a device."
        });
        return;
      }

      const normalizedDeviceId = deviceID.trim();
      const normalizedName = deviceName.trim();
      const result = await addDeviceForUser(user.uid, normalizedDeviceId, normalizedName);

      if (!result.ok) {
        if (result.code === "already_owned") {
          showPopup({
            type: "warning",
            title: "Already Added",
            message: "This device is already registered to your account."
          });
        } else if (result.code === "owned_by_other") {
          showPopup({
            type: "warning",
            title: "Device In Use",
            message: "This device is already registered to another account."
          });
        } else if (result.code === "not_provisioned") {
          showPopup({
            type: "error",
            title: "Unknown Device ID",
            message: "This device ID is not registered yet. Contact support with your device ID so it can be added to the system."
          });
        } else if (result.code === "device_disabled") {
          showPopup({
            type: "error",
            title: "Device Disabled",
            message: "This device has been disabled. Contact support for help."
          });
        }
        return;
      }

      logInfo("Dashboard", "Device added successfully", {
        uid: user.uid,
        deviceId: normalizedDeviceId
      });

      showPopup({
        type: "success",
        title: "Device Added",
        message: "Device added successfully."
      });
      toast.success("Device added.");
      setDeviceID("");
      setDeviceName("");
    } catch (error) {
      logError("Dashboard", "Failed to add device", error, {
        uid: user?.uid || null,
        deviceId: deviceID
      });
      showPopup({
        type: "error",
        title: "Add Device Failed",
        message: "Unable to add device right now. Please retry."
      });
      toast.error("Add device failed");
    }
  };

  const openEditDevicePopup = (id) => {
    setEditingDeviceId(id);
    setEditingDeviceName(userDevices[id]?.name || id);
  };

  const closeEditDevicePopup = () => {
    setEditingDeviceId("");
    setEditingDeviceName("");
  };

  const handleSaveDeviceName = async () => {
    if (!user || !editingDeviceId) return;
    if (!editingDeviceName.trim()) {
      showPopup({
        type: "warning",
        title: "Invalid Name",
        message: "Device name cannot be empty."
      });
      return;
    }

    try {
      await renameUserDevice(user.uid, editingDeviceId, editingDeviceName);
      showPopup({
        type: "success",
        title: "Device Updated",
        message: "Device name updated successfully."
      });
      toast.success("Device renamed.");
      closeEditDevicePopup();
    } catch (error) {
      logError("Dashboard", "Failed to update device name", error, {
        uid: user.uid,
        deviceId: editingDeviceId
      });
      showPopup({
        type: "error",
        title: "Update Failed",
        message: "Unable to update device name right now."
      });
      toast.error("Rename failed");
    }
  };

  return (
    <PageShell>
      <PageHero
        eyebrow="App"
        title="Dashboard."
        highlight="Live telemetry."
        description="Real-time overview of device health and sensor signals. Rename devices, jump to history, and keep an eye on alerts."
      />

      <PageSection variant="content" className="!pb-10">
        <div className="mx-auto max-w-6xl grid gap-6">
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="shadow-soft">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-accent" />
                  Fleet summary
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <div className="flex items-center justify-between">
                  <span>Total devices</span>
                  <span className="font-medium text-foreground">{cards.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Online now</span>
                  <span className="font-medium text-foreground">{cards.filter((c) => c.online).length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Offline</span>
                  <span className="font-medium text-foreground">{cards.filter((c) => !c.online).length}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-soft md:col-span-2">
              <CardHeader>
                <CardTitle className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <span className="flex items-center gap-2">
                    <Cpu className="h-5 w-5 text-accent" />
                    Quick actions
                  </span>
                  <div className="flex flex-col gap-2 w-full sm:w-auto sm:flex-row">
                    <Button variant="outline" onClick={() => toast("Tip", { description: "Use Devices page for bulk actions and filters." })}>
                      Open tips
                    </Button>
                    <Button onClick={() => (window.location.href = "/devices")}>
                      Manage devices <ArrowUpRight className="h-4 w-4" />
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-3 gap-3 text-sm">
                <div className="rounded-2xl border border-border bg-cream/50 p-4">
                  <div className="text-muted-foreground">History & exports</div>
                  <div className="mt-2 font-medium text-foreground">Trends</div>
                  <Button variant="link" className="mt-2" onClick={() => (window.location.href = "/history")}>
                    View history <ArrowUpRight className="h-4 w-4" />
                  </Button>
                </div>
                <div className="rounded-2xl border border-border bg-cream/50 p-4">
                  <div className="text-muted-foreground">Alerts</div>
                  <div className="mt-2 font-medium text-foreground">Rules</div>
                  <Button variant="link" className="mt-2" onClick={() => (window.location.href = "/alerts")}>
                    Configure <ArrowUpRight className="h-4 w-4" />
                  </Button>
                </div>
                <div className="rounded-2xl border border-border bg-cream/50 p-4">
                  <div className="text-muted-foreground">Device settings</div>
                  <div className="mt-2 font-medium text-foreground">Wi‑Fi</div>
                  <Button variant="link" className="mt-2" onClick={() => (window.location.href = "/settings")}>
                    Open settings <ArrowUpRight className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="shadow-soft">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5 text-accent" />
                  Add device
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <input
                  className="w-full rounded-2xl border border-input bg-cream/50 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Device ID (e.g., O3 — must be pre-registered)"
                  value={deviceID}
                  onChange={(e) => setDeviceID(e.target.value)}
                />
                <input
                  className="w-full rounded-2xl border border-input bg-cream/50 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Device Name (e.g., Greenhouse 1)"
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                />
                <Button className="w-full" size="lg" onClick={handleAddDevice}>
                  Add device
                </Button>
                <p className="text-xs text-muted-foreground">
                  Only device IDs registered by support can be linked. Each ID works on one account only. Configure Wi-Fi per device in Settings after linking.
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-soft">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-accent" />
                  Live devices
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                Cards update in real-time as devices publish telemetry. Online/offline is based on last-seen.
              </CardContent>
            </Card>
          </div>
        </div>
      </PageSection>

      <PageSection variant="content">
        <div className="mx-auto max-w-6xl grid lg:grid-cols-2 gap-6">
          {cards.map((d) => {
            const sens = devicePreviewSensors(d.deviceType);
            const tileCols = [sens.temp, sens.humidity, sens.soil].filter(Boolean).length || 1;
            return (
            <div key={d.id} className="rounded-3xl border border-border bg-card shadow-soft p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-display text-2xl">{d.name}</div>
                  <div className="mt-1 text-xs text-muted-foreground">ID: {d.id}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2 justify-end">
                  <span className="inline-flex items-center rounded-full bg-secondary px-3 py-1 text-xs text-muted-foreground">
                    {deviceTypeLabel(d.deviceType)}
                  </span>
                  {d.isSimulator ? (
                    <span className="inline-flex items-center gap-2 rounded-full bg-secondary text-muted-foreground px-3 py-1 text-xs">
                      <Cpu className="h-4 w-4" /> Simulator
                    </span>
                  ) : d.online ? (
                    <span className="inline-flex items-center gap-2 rounded-full bg-success/15 text-success px-3 py-1 text-xs">
                      <Wifi className="h-4 w-4" /> Online
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2 rounded-full bg-destructive/15 text-destructive px-3 py-1 text-xs">
                      <WifiOff className="h-4 w-4" /> Offline
                    </span>
                  )}
                </div>
              </div>

              <div
                className={`mt-5 grid gap-3 ${
                  tileCols === 1 ? "grid-cols-1" : tileCols === 2 ? "grid-cols-2" : "grid-cols-3"
                }`}
              >
                {sens.temp && (
                  <div className="rounded-2xl border border-border bg-cream/50 p-4">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Thermometer className="h-4 w-4" /> Temp
                    </div>
                    <div className="mt-2 font-display text-2xl text-foreground">
                      {d.t ?? "—"}
                      {d.t !== undefined ? <span className="text-sm text-muted-foreground">°C</span> : null}
                    </div>
                  </div>
                )}
                {sens.humidity && (
                  <div className="rounded-2xl border border-border bg-cream/50 p-4">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Droplets className="h-4 w-4" /> Humidity
                    </div>
                    <div className="mt-2 font-display text-2xl text-foreground">
                      {d.h ?? "—"}
                      {d.h !== undefined ? <span className="text-sm text-muted-foreground">%</span> : null}
                    </div>
                  </div>
                )}
                {sens.soil && (
                  <div className="rounded-2xl border border-border bg-cream/50 p-4">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Droplets className="h-4 w-4" /> Soil
                    </div>
                    <div className="mt-2 font-display text-2xl text-foreground">
                      {d.s ?? "—"}
                      {d.s !== undefined ? <span className="text-sm text-muted-foreground">%</span> : null}
                    </div>
                  </div>
                )}
              </div>

              {!d.isSimulator && d.pumpConnected && (
                <div className="mt-5">
                  <PumpControl
                    deviceId={d.id}
                    deviceLabel={d.name}
                    online={d.online}
                    canControl={canControlPump}
                    liveReading={{ t: d.t, h: d.h, s: d.s }}
                  />
                </div>
              )}
              {!d.isSimulator && !d.pumpConnected && d.deviceType !== "node" && (
                <p className="mt-5 text-xs text-muted-foreground">
                  Pump controls hidden until a pump is connected. Manage this on the Devices page.
                </p>
              )}
              {!d.isSimulator && d.deviceType === "node" && (
                <p className="mt-5 text-xs text-muted-foreground">
                  Pot node — irrigate from its Hub. Open Devices → hub → Add node.
                </p>
              )}

              <div className="mt-5 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                <Button className="w-full sm:w-auto" size="sm" variant="outline" onClick={() => openEditDevicePopup(d.id)}>
                  Rename
                </Button>
                <Button className="w-full sm:w-auto" size="sm" variant="outline" onClick={() => (window.location.href = "/history")}>
                  History <ArrowUpRight className="h-3.5 w-3.5" />
                </Button>
                <Button className="w-full sm:w-auto" size="sm" variant="outline" onClick={() => (window.location.href = "/settings")}>
                  Settings <ArrowUpRight className="h-3.5 w-3.5" />
                </Button>
                <Button className="w-full sm:w-auto" size="sm" variant="link" onClick={() => (window.location.href = "/devices")}>
                  Devices center <ArrowUpRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            );
          })}

          {!cards.length && (
            <div className="lg:col-span-2 rounded-3xl border border-border bg-card p-10 shadow-soft text-center">
              <div className="font-display text-2xl">No devices linked yet</div>
              <p className="mt-2 text-sm text-muted-foreground">
                Add your first device above to start seeing live telemetry.
              </p>
            </div>
          )}
        </div>
      </PageSection>

      {editingDeviceId && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-6">
          <div className="w-full max-w-lg rounded-3xl border border-border bg-card p-8 shadow-elevated">
            <div className="font-display text-2xl">Rename device</div>
            <p className="mt-2 text-sm text-muted-foreground">
              Update display name for <span className="font-mono text-foreground">{editingDeviceId}</span>.
            </p>
            <input
              className="mt-6 w-full rounded-2xl border border-input bg-cream/50 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-ring"
              type="text"
              value={editingDeviceName}
              onChange={(e) => setEditingDeviceName(e.target.value)}
              placeholder="Device name"
            />
            <div className="mt-6 flex gap-3 justify-end">
              <Button variant="outline" onClick={closeEditDevicePopup}>
                Cancel
              </Button>
              <Button onClick={handleSaveDeviceName}>Save</Button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
